import React, { useEffect, useState, useCallback } from 'react';
import { Users, Download, Plus, KeyRound } from 'lucide-react';
import { exportCsv } from '../../utils/exportCsv';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import SearchInput from '../../components/common/SearchInput';
import Select from '../../components/common/Select';
import Badge from '../../components/common/Badge';
import StatusBadge from '../../components/common/StatusBadge';
import DataTable from '../../components/common/DataTable';
import EmptyState from '../../components/common/EmptyState';
import Avatar from '../../components/common/Avatar';
import { storeUsersService } from '../../services/storeUsersService';
import { useToast } from '../../contexts/ToastContext';
import { useStoreContext } from '../../contexts/StoreContext';
import StoreUserFormModal from './StoreUserFormModal';
import { formatRole } from '../../utils/enums';

const PAGE_SIZE = 20;

const StoreUsersManagement = () => {
  const { error, success } = useToast();
  const { store: currentStore } = useStoreContext();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({ role: '', status: '', q: '', active: '' });
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const isReseller = currentStore?.businessType === 'reseller';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await storeUsersService.list({ ...filter, page, limit: PAGE_SIZE });
      setRows(data?.rows || []);
      setTotal(data?.count || 0);
    } catch (e) {
      error(e.message);
    } finally {
      setLoading(false);
    }
  }, [filter, page, error]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (userId, status, active = true) => {
    try {
      await storeUsersService.updateStatus(userId, { status, active });
      success(`Usuário ${status === 'active' ? 'ativado' : 'suspenso'}`);
      load();
    } catch (e) {
      error(e.message);
    }
  };

  const sendReset = async (userId) => {
    try {
      await storeUsersService.resetPassword(userId);
      success('Email de reset enviado');
    } catch (e) {
      error(e.message);
    }
  };

  const columns = [
    {
      key: 'user',
      header: 'Usuário',
      render: (u) => (
        <div className="flex items-center gap-3">
          <Avatar name={u.name} size="sm" />
          <div className="min-w-0">
            <p className="font-semibold text-app-text truncate">{u.name}</p>
            <p className="text-xs text-app-text-secondary truncate">{u.email}</p>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      header: 'Papel',
      render: (u) => {
        const membership = (u.memberships || []).find(
          (m) => m.storeId === currentStore?.id
        );
        return (
          <div className="flex flex-col gap-1">
            <Badge color="neutral" variant="soft" size="sm">
              {formatRole(u.role)}
            </Badge>
            {membership && (
              <span className="text-xs text-app-text-secondary">
                Vínculo: {membership.roleInStore}
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      render: (u) => (
        <div className="flex items-center gap-2">
          <StatusBadge domain="user" status={u.status} />
          {!u.active && (
            <Badge color="error" size="sm" variant="soft">
              Inativo
            </Badge>
          )}
        </div>
      )
    },
    {
      key: 'phone',
      header: 'Telefone',
      render: (u) => <span className="text-sm text-app-text-secondary">{u.phone || '—'}</span>
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (u) => (
        <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" leftIcon={<KeyRound size={14} />} onClick={() => sendReset(u.id)}>
            Reset
          </Button>
          <Button size="sm" variant="subtle" onClick={() => setEditing(u)}>
            Editar
          </Button>
          {u.status !== 'active' && (
            <Button size="sm" variant="subtle" onClick={() => changeStatus(u.id, 'active', true)}>
              Ativar
            </Button>
          )}
          {u.status !== 'suspended' && (
            <Button size="sm" variant="danger" onClick={() => changeStatus(u.id, 'suspended', false)}>
              Suspender
            </Button>
          )}
        </div>
      )
    }
  ];

  const clearFilters = () => {
    setFilter({ role: '', status: '', q: '', active: '' });
    setPage(1);
  };

  const roleOptions = [
    { value: '', label: 'Todos papéis' },
    { value: 'store_owner', label: 'Manager / Staff' },
    { value: 'client', label: 'Cliente' }
  ];
  if (isReseller) roleOptions.push({ value: 'reseller', label: 'Revendedor' });

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-app-text">Usuários da loja</h1>
          <p className="text-sm text-app-text-secondary mt-1">
            Cadastre managers, clientes{isReseller ? ' e revendedores' : ''} vinculados à sua loja.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
            Novo usuário
          </Button>
          <Button
            variant="subtle"
            leftIcon={<Download size={16} />}
            onClick={() =>
              exportCsv({
                filename: 'usuarios-loja.csv',
                columns: [
                  { key: 'id', header: 'ID' },
                  { key: 'name', header: 'Nome' },
                  { key: 'email', header: 'Email' },
                  { key: 'role', header: 'Papel', format: (r) => formatRole(r) },
                  { key: 'status', header: 'Status' },
                  { key: 'active', header: 'Ativo', format: (v) => (v ? 'Sim' : 'Não') },
                  { key: 'phone', header: 'Telefone' }
                ],
                rows
              })
            }
            disabled={!rows.length}
          >
            Exportar CSV
          </Button>
        </div>
      </div>

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <SearchInput
              value={filter.q}
              onChange={(q) => {
                setFilter((f) => ({ ...f, q }));
                setPage(1);
              }}
              placeholder="Buscar nome, email ou CPF…"
              debounceMs={300}
            />
          </div>
          <Select
            value={filter.role}
            onChange={(e) => {
              setFilter((f) => ({ ...f, role: e.target.value }));
              setPage(1);
            }}
            options={roleOptions}
          />
          <Select
            value={filter.status}
            onChange={(e) => {
              setFilter((f) => ({ ...f, status: e.target.value }));
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todos status' },
              { value: 'active', label: 'Ativo' },
              { value: 'suspended', label: 'Suspenso' },
              { value: 'pending', label: 'Pendente' }
            ]}
          />
          <Select
            value={filter.active}
            onChange={(e) => {
              setFilter((f) => ({ ...f, active: e.target.value }));
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todos (ativos+inativos)' },
              { value: 'true', label: 'Apenas ativos' },
              { value: 'false', label: 'Apenas inativos' }
            ]}
          />
        </div>
        {(filter.q || filter.role || filter.status || filter.active) && (
          <div className="mt-3">
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Limpar filtros
            </Button>
          </div>
        )}
      </Card>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        empty={
          <EmptyState
            icon={Users}
            title="Nenhum usuário cadastrado nesta loja"
            description="Cadastre o primeiro usuário pra começar."
          />
        }
      />

      <StoreUserFormModal
        isOpen={createOpen || !!editing}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
        }}
        user={editing}
        isResellerStore={isReseller}
        onSaved={load}
      />
    </div>
  );
};

export default StoreUsersManagement;
